export interface BenefactorModel {
  id: string;
  displayName: string;
  realName: string | null;
  phoneNumber: string | null;
  email: string | null;
  createdAt: string;
  lockedAt: string | null;
  deletedAt: string | null;
  lastLoggedInAt: string | null;
  avatarUrl: string | null;
}

export interface BenefactorWithMetaModel {
  items: BenefactorModel[];
  totalCount: number;
  pageInfo: PageInfoModel;
}

export interface BenefactorsFilter {
  query?: string;
  after?: string;
  pageSize?: number;
}

export interface CreateBenefactorDTO {
  displayName: string | null;
  realName: string;
}

export interface EditBenefactorNamesParams {
  benefactorId: string;
  displayName: string | null;
  realName: string | null;
}

export interface EditBenefactorNamesDTO {
  displayName: string | null;
  realName: string | null;
}

export interface EditBenefactorEmailParams {
  benefactorId: string;
  email: string;
}

export interface EditBenefactorEmailDTO {
  email: string;
}

export interface EditBenefactorPhoneParams {
  benefactorId: string;
  phoneNumber: string;
}

export interface EditBenefactorPhoneDTO {
  phoneNumber: string;
}

// Order types
export enum BenefactorOrderStatus {
  Created = 'Created',
  PreparedForPayment = 'PreparedForPayment',
  PaymentInProgress = 'PaymentInProgress',
  PaymentChallenge = 'PaymentChallenge',
  PaymentChallengeResponse = 'PaymentChallengeResponse',
  PaymentOutcomeReceived = 'PaymentOutcomeReceived',
  PaymentSuccess = 'PaymentSuccess',
  PaymentFailed = 'PaymentFailed',
  Expired = 'Expired',
  Fulfilled = 'Fulfilled',
  Failed = 'Failed',
}

export interface BenefactorOrderModel {
  id: string;
  amount: number;
  status: BenefactorOrderStatus;
  isSucceeded: boolean;
  isFailed: boolean;
  failureMessage: string | null;
  createdAt: string;
}

export interface BenefactorOrderWithMetaModel {
  items: BenefactorOrderModel[];
  totalCount: number;
  pageInfo: PageInfoModel;
}

export interface BenefactorOrdersFilter {
  query?: string;
  page?: number;
  pageSize?: number;
  after?: string;
  sortProperty?: 'amount' | 'createdAt' | 'status';
  sortOrder?: 'ascending' | 'descending';
}

// Детальная модель заказа
export interface DonationIntent {
  type: string;
  appealId?: string;
  amount: number;
  serviceFee: number;
}

export interface PaymentInformation {
  type: string;
  bookkeepingNumber?: string;
  note?: string;
}

export interface PaymentChallenge {
  type: string;
  transactionId?: number;
  paReq?: string;
  acsUrl?: string;
}

export interface PaymentChallengeResponse {
  type: string;
  paRes?: string;
}

export interface PaymentResult {
  type: string;
  message?: string;
}

export interface AmountSummary {
  oneTimeAppeal: number;
  oneTimeProject: number;
  projectSubscription: number;
  projectSubscriptionCharge: number;
  projectServiceFee: number;
  paymentSystemFee: number;
  total: number;
}

export interface OrderDetailModel {
  id: string;
  donationIntent: DonationIntent;
  paymentInformation: PaymentInformation;
  paymentChallenge: PaymentChallenge;
  paymentChallengeResponse: PaymentChallengeResponse;
  paymentResult: PaymentResult;
  intendedAmountSummary: AmountSummary;
  actualAmountSummary: AmountSummary;
  status: BenefactorOrderStatus;
  isSucceeded: boolean;
  isFailed: boolean;
  failureMessage: string | null;
  createdAt: string;
}

export type BenefactorPaymentsInfoModel = {
  id: string;
  isDefault: boolean;
  addedAt: string;
} & (
  | {
      type: 'CloudPaymentsSavedCard';
      cardType: string;
      firstSix: string;
      lastFour: string;
      expirationDate: string;
      cardholderName: string;
    }
  | {
      type: 'CloudPaymentsSbpAccount';
      bankName: string | null;
    }
);

// Subscription types
export enum SubscriptionStatus {
  Active = 'Active',
  Inactive = 'Inactive',
}

export enum SubscriptionFilter {
  All = 'All',
  Active = 'Active',
  Inactive = 'Inactive',
}

export enum CancellationReason {
  CancelledByBenefactor = 'CancelledByBenefactor',
  CancelledByAdmin = 'CancelledByAdmin',
  PaymentFailure = 'PaymentFailure',
  Other = 'Other',
}

export interface BenefactorSubscriptionModel {
  id: string;
  amount: number;
  cancellationReason: CancellationReason | null;
  cancelledAt: string | null;
  lastChargedAt: string | null;
  nextChargeAt: string | null;
  lastFailedAttemptAt: string | null;
  numberOfFailedAttempts: number | null;
  createdAt: string;
  type: string;
}

export interface BenefactorSubscriptionsWithMetaModel {
  items: BenefactorSubscriptionModel[];
  pageInfo: PageInfoModel;
  totalCount: number;
}

export interface BenefactorSubscriptionsFilter {
  filter: SubscriptionFilter;
  first?: number | null;
  after?: string | null;
  last?: number | null;
  before?: string | null;
  includeTotalCount?: boolean | null;
}

// Scoring types
export interface BenefactorScoringModel {
  id: string;
  points: number;
  pointsMultiplier: number;
  ratingPosition: number | null;
}

export interface PointsAssignmentModel {
  reason: string;
  orderId: string | null;
  points: number;
  createdAt: string;
}

export interface PointsAssignmentWithMetaModel {
  items: PointsAssignmentModel[];
  pageInfo: PageInfoModel;
  totalCount: number;
}

export interface PointsAssignmentFilter {
  pageSize?: number;
  after?: string;
}
